import { useCallback, useEffect, useState } from 'react'
import type { BoxTruckDriverAssignmentsDto } from '@numeo/types-core'
import { trackBoxTruckUiInteraction } from '../utils/analytics'
import { fetchBoxTruckDriverAssignments } from '../services/boxTruckLoadsApi'

interface AssignmentsState {
    /** The driver this state belongs to. Nothing is rendered from it under any other driver. */
    userId?: string
    data: BoxTruckDriverAssignmentsDto | null
    failed: boolean
}

interface DriverAssignments {
    /** Null while the answer for THIS driver is not in hand — loading, failed, or not asked for. */
    data: BoxTruckDriverAssignmentsDto | null
    loading: boolean
    /** The request was refused or never answered. NOT the same as "nothing is assigned". */
    failed: boolean
    reload: () => void
}

const IDLE: AssignmentsState = { data: null, failed: false }

/**
 * The owner and the vehicle a driver is attached to, fetched when that driver is opened.
 *
 * **Keyed by driver, not merely re-fetched per driver.** The obvious shape — one `data` state and an effect
 * that clears it — paints the *next* driver with the *previous* one's owner, vehicle and document ids for
 * the render between selection and the effect, because effects run after paint. On that frame the document
 * chips are not just wrong, they are live: clicking one addresses the previous driver's records. Carrying
 * the id inside the state and refusing to answer for any other driver removes the window rather than
 * shortening it.
 *
 * **A failure is not an empty answer.** Collapsing a 403, a 500 or a dropped connection into the same DTO
 * as "this driver has no owner" tells the operator something false about the driver, with nothing to retry.
 * `failed` is separate so the screen can say which of the two happened.
 *
 * `enabled` exists for the cross-tenant Driver Verifications queue: assignments resolve from the caller's
 * claim, so for a row nobody here has added the answer is empty by construction and the request is not
 * worth making.
 */
export const useDriverAssignments = (userId?: string, enabled = true): DriverAssignments => {
    const [state, setState] = useState<AssignmentsState>(IDLE)
    const [reloadKey, setReloadKey] = useState(0)
    const reload = useCallback(() => {
        trackBoxTruckUiInteraction('driver_assignments_retried')
        setReloadKey((key) => key + 1)
    }, [])

    useEffect(() => {
        if (!userId || !enabled) {
            setState(IDLE)
            return
        }
        let cancelled = false
        setState({ userId, data: null, failed: false })
        fetchBoxTruckDriverAssignments(userId)
            .then((data) => !cancelled && setState({ userId, data, failed: false }))
            .catch((error) => {
                if (cancelled) return
                // Both, and they answer different questions. The console line is for whoever is debugging
                // ONE report — a 403 from the RBAC-vs-Profiles grant mismatch and a 500 from the query behind
                // it surface as the same banner. The event is the RATE: that 403 hits a whole role at once,
                // and nothing else would show it.
                console.error('[box-truck] driver assignments failed', error)
                trackBoxTruckUiInteraction('driver_assignments_failed')
                setState({ userId, data: null, failed: true })
            })
        return () => {
            cancelled = true
        }
    }, [userId, enabled, reloadKey])

    // The guard that makes the key worth carrying: a state belonging to another driver answers nothing.
    const mine = state.userId === userId ? state : IDLE
    return {
        data: mine.data,
        loading: !!userId && enabled && !mine.data && !mine.failed,
        failed: mine.failed,
        reload,
    }
}
